<template>
  <div
    v-if="!isStepOpen"
    class="relative flex flex-col w-full gap-4 p-5 bg-white dark:bg-gray-800 rounded-lg shadow-lg shadow-blue-gray-900/5 transition-all"
  >
    <div role="alert" class="relative w-full">
      <div class="max-w-4xl mx-auto p-4">
        <transition name="fade" mode="out-in">
          <div
            :key="currentStep"
            class="flex flex-col justify-center items-center text-center"
          >
            <img
              :src="steps[currentStep].image"
              alt="Step Image"
              class="w-full max-w-md h-auto mb-4 rounded-lg shadow-md"
            />
            <h3
              class="text-[clamp(1rem,2vw,1.5rem)] font-semibold leading-snug tracking-wide text-gray-900 dark:text-gray-200 mb-2"
            >
              {{ steps[currentStep].title }}
            </h3>
            <p
              class="text-[clamp(0.875rem,1.5vw,1rem)] leading-6 opacity-90 text-gray-700 dark:text-gray-300 mb-4"
            >
              {{ steps[currentStep].description }}
            </p>
          </div>
        </transition>

        <!-- Progress Bar -->
        <div class="relative mb-6 w-full max-w-sm sm:max-w-md mx-auto">
          <div
            class="absolute w-full h-1 bg-gray-300 dark:bg-gray-600 top-1/2 -translate-y-1/2"
          ></div>
          <div
            class="absolute h-1 bg-blue-600 dark:bg-sub-700 top-1/2 -translate-y-1/2 transition-all duration-500"
            :style="{ width: `${(currentStep / (steps.length - 1)) * 100}%` }"
          ></div>
          <div class="flex justify-between relative z-10">
            <div
              v-for="(step, index) in steps"
              :key="index"
              class="w-8 h-8 rounded-full border-4 flex items-center justify-center text-sm font-medium transition-all"
              :class="{
                'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300':
                  index > currentStep,
                'border-blue-600 dark:border-sub-700 text-blue-600 dark:text-gray-50 dark:bg-gray-700':
                  index <= currentStep,
              }"
            >
              {{ index + 1 }}
            </div>
          </div>
        </div>

        <!-- Buttons -->
        <div class="flex justify-between mt-4 gap-3 w-fit mx-auto">
          <button
            @click="prevStep"
            :disabled="currentStep === 0"
            class="p-2 px-4 rounded-lg text-sm font-medium transition-all disabled:opacity-50 bg-gray-300 hover:bg-gray-400 text-gray-800 dark:bg-gray-600 dark:hover:bg-gray-500 dark:text-gray-200"
          >
            <i
              class="fa-solid fa-angles-left text-[clamp(1rem,1.5vw,1.125rem)]"
            ></i>
          </button>

          <button
            v-if="currentStep < steps.length - 1"
            @click="nextStep"
            class="p-2 px-4 rounded-lg text-sm font-medium transition-all bg-blue-600 text-white hover:bg-blue-700 dark:bg-sub-900 dark:hover:bg-blue-500"
          >
            Next
          </button>
          <button
            v-else
            @click="closeStep"
            class="p-2 px-4 rounded-lg text-sm font-medium transition-all bg-blue-600 text-white hover:bg-blue-700 dark:bg-sub-800 dark:hover:bg-blue-500"
          >
            Finish
          </button>
        </div>
      </div>
    </div>

    <!-- Close Button -->
    <button
      @click="closeStep"
      aria-label="Close step"
      class="absolute top-3 right-3 flex items-center justify-center h-8 w-8 rounded-lg transition-all bg-gray-100 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200"
    >
      <i class="fa-solid fa-xmark text-[clamp(1rem,1.5vw,1.125rem)]"></i>
    </button>
  </div>
</template>

<script>
export default {
  name: "StepForm",
  data() {
    return {
      currentStep: 0,
      userId: this.$store.state.user,
      isStepOpen: false,
      steps: [
        {
          title: "Step 1: Introduction",
          description:
            "Welcome to your dashboard! This is your control center where you can monitor store performance, manage multiple stores, and access key features. If you need help, click the Helpdesk button at the bottom.",
          image: "https://via.placeholder.com/400x200?text=Step+1",
        },
        {
          title: "Step 2: Store Setup",
          description:
            "Start by creating your store. Provide essential details such as name, type (e.g., clothing, electronics, food), and physical address if applicable. A well-detailed profile builds trust and helps customers find you.",
          image: "https://via.placeholder.com/400x200?text=Step+2",
        },
        {
          title: "Step 3: Adding Products",
          description:
            "Populate your store by adding products. Your store will not be visible until at least one product is listed. Upload images, set prices, and add descriptions to attract buyers.",
          image: "https://via.placeholder.com/400x200?text=Step+3",
        },
        {
          title: "Step 4: Store Promotion",
          description:
            "Boost your store’s visibility using the 'Boost Store' feature. Advertise your store or specific products for increased reach. Paid subscriptions receive automated promotion for better exposure.",
          image: "https://via.placeholder.com/400x200?text=Step+4",
        },
      ],
    };
  },
  mounted() {
    this.isStepOpen = this.$store.getters.isStepOpen || false;
  },
  methods: {
    closeStep() {
      this.isStepOpen = true;
      localStorage.setItem(`${this.userId}isStepOpen`, this.isStepOpen);
    },
    nextStep() {
      if (this.currentStep < this.steps.length - 1) {
        this.currentStep++;
      }
    },
    prevStep() {
      if (this.currentStep > 0) {
        this.currentStep--;
      }
    },
  },
};
</script>

<style scoped>
/* Fade transition for step changes */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

/* Smooth transition for dark mode */
@media (prefers-color-scheme: dark) {
  div {
    transition:
      background-color 0.3s ease,
      color 0.3s ease;
  }
}
</style>
